/**
 * GridCanvas Component
 * Drag-and-drop grid builder for form field layout.
 * Mirrors the react-grid-layout pattern from Monsoon's Dashboard.
 * Used in the FormBuilder builder mode only — not in DynamicForm rendering.
 */

import * as React from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout/legacy';
import type { Layout, LayoutItem } from 'react-grid-layout/legacy';
import {
  Button,
  Text,
  Badge,
  makeStyles,
  tokens,
  mergeClasses,
} from '@fluentui/react-components';
import {
  EditRegular,
  DeleteRegular,
  ReOrderDotsVerticalRegular,
} from '@fluentui/react-icons';
import type { FieldDefinition } from '@structured-request/core';
import { FIELD_TYPE_INFO } from '@structured-request/core';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

const ResponsiveGridLayout = WidthProvider(Responsive);

const useStyles = makeStyles({
  wrapper: {
    minHeight: '300px',
    width: '100%',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    padding: tokens.spacingVerticalS,
  },
  emptyState: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '240px',
    color: tokens.colorNeutralForeground3,
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  fieldCard: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    overflow: 'hidden',
    height: '100%',
    boxSizing: 'border-box',
    userSelect: 'none',
  },
  fieldCardSelected: {
    border: `2px solid ${tokens.colorBrandStroke1}`,
  },
  dragHandle: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    backgroundColor: tokens.colorNeutralBackground3,
    cursor: 'grab',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    minHeight: '28px',
    '&:active': {
      cursor: 'grabbing',
    },
  },
  dragHandleStatic: {
    cursor: 'default',
  },
  fieldBody: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    gap: tokens.spacingVerticalXS,
    overflow: 'hidden',
  },
  fieldLabel: {
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  fieldMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    flexWrap: 'wrap',
  },
  fieldActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: tokens.spacingHorizontalXS,
    padding: `0 ${tokens.spacingHorizontalXS} ${tokens.spacingVerticalXS}`,
  },
  inputPreview: {
    height: '24px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusSmall,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    marginTop: 'auto',
  },
  fieldCardSpacer: {
    backgroundColor: 'transparent',
    borderRadius: tokens.borderRadiusMedium,
    border: `2px dashed ${tokens.colorNeutralStroke2}`,
    height: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    userSelect: 'none',
    opacity: '0.6',
  },
});

export interface GridLayoutUpdate {
  id: string;
  gridX: number;
  gridY: number;
  gridW: number;
  gridH: number;
}

export interface GridCanvasProps {
  fields: FieldDefinition[];
  gridColumns: number;
  isEditing: boolean;
  selectedFieldId?: string | null;
  onLayoutChange: (updates: GridLayoutUpdate[]) => void;
  onFieldSelect: (fieldId: string) => void;
  onFieldDelete: (fieldId: string) => void;
}

export const GridCanvas: React.FC<GridCanvasProps> = ({
  fields,
  gridColumns,
  isEditing,
  selectedFieldId,
  onLayoutChange,
  onFieldSelect,
  onFieldDelete,
}) => {
  const styles = useStyles();

  const layout: LayoutItem[] = fields.map((field) => ({
    i: field.id,
    x: field.gridX ?? 0,
    y: field.gridY ?? (field.order ?? 0),
    w: Math.min(field.gridW ?? 1, gridColumns),
    h: field.gridH ?? 2,
    minW: 1,
    minH: 1,
  }));

  const cols = {
    lg: gridColumns,
    md: gridColumns,
    sm: gridColumns,
    xs: gridColumns,
    xxs: gridColumns,
  };

  const handleLayoutChange = (newLayout: Layout) => {
    if (!isEditing) return;
    const updates: GridLayoutUpdate[] = newLayout.map((item) => ({
      id: item.i,
      gridX: item.x,
      gridY: item.y,
      gridW: item.w,
      gridH: item.h,
    }));
    onLayoutChange(updates);
  };

  if (fields.length === 0) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.emptyState}>
          <Text size={300} style={{ color: tokens.colorNeutralForeground3 }}>
            No fields yet
          </Text>
          <Text size={200} style={{ color: tokens.colorNeutralForeground4 }}>
            Add fields from the panel on the left
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.wrapper}>
      <ResponsiveGridLayout
        className="layout"
        layouts={{ lg: layout, md: layout, sm: layout, xs: layout, xxs: layout }}
        breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
        cols={cols}
        rowHeight={100}
        isDraggable={isEditing}
        isResizable={isEditing}
        onLayoutChange={handleLayoutChange}
        draggableHandle=".drag-handle"
        compactType="vertical"
        margin={[8, 8] as [number, number]}
      >
        {fields.map((field) => {
          const info = FIELD_TYPE_INFO[field.type];
          const isSelected = selectedFieldId === field.id;

          // Spacer fields render as a minimal dashed placeholder
          if (field.type === 'spacer') {
            return (
              <div key={field.id}>
                <div
                  className={mergeClasses(
                    styles.fieldCardSpacer,
                    isSelected && styles.fieldCardSelected
                  )}
                  onClick={() => onFieldSelect(field.id)}
                >
                  <div
                    className={mergeClasses(
                      styles.dragHandle,
                      'drag-handle',
                      !isEditing && styles.dragHandleStatic
                    )}
                  >
                    <ReOrderDotsVerticalRegular
                      style={{ fontSize: '14px', color: tokens.colorNeutralForeground3, flexShrink: 0 }}
                    />
                    <Text size={100} style={{ flex: 1, color: tokens.colorNeutralForeground4, fontStyle: 'italic' }}>
                      spacer
                    </Text>
                  </div>
                  <div className={styles.fieldActions} onClick={(e) => e.stopPropagation()}>
                    <Button
                      icon={<DeleteRegular />}
                      appearance="subtle"
                      size="small"
                      onClick={() => onFieldDelete(field.id)}
                    />
                  </div>
                </div>
              </div>
            );
          }

          return (
            <div key={field.id}>
              <div
                className={mergeClasses(
                  styles.fieldCard,
                  isSelected && styles.fieldCardSelected
                )}
                onClick={() => onFieldSelect(field.id)}
              >
                {/* Drag handle — only draggable from here */}
                <div
                  className={mergeClasses(
                    styles.dragHandle,
                    'drag-handle',
                    !isEditing && styles.dragHandleStatic
                  )}
                >
                  <ReOrderDotsVerticalRegular
                    style={{
                      fontSize: '14px',
                      color: tokens.colorNeutralForeground3,
                      flexShrink: 0,
                    }}
                  />
                  <Text
                    size={200}
                    weight="semibold"
                    truncate
                    style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {field.label}
                  </Text>
                  {field.required && (
                    <Text size={200} style={{ color: tokens.colorPaletteRedForeground1, flexShrink: 0 }}>
                      *
                    </Text>
                  )}
                </div>

                {/* Field body */}
                <div className={styles.fieldBody}>
                  <div className={styles.fieldMeta}>
                    <Badge appearance="outline" size="small" color="informative">
                      {info.label}
                    </Badge>
                    {field.sectionId && (
                      <Badge appearance="outline" size="small" color="subtle">
                        §
                      </Badge>
                    )}
                  </div>
                  {/* Visual input preview */}
                  <div className={styles.inputPreview} />
                </div>

                {/* Actions */}
                <div className={styles.fieldActions} onClick={(e) => e.stopPropagation()}>
                  <Button
                    icon={<EditRegular />}
                    appearance="subtle"
                    size="small"
                    onClick={() => onFieldSelect(field.id)}
                  />
                  <Button
                    icon={<DeleteRegular />}
                    appearance="subtle"
                    size="small"
                    onClick={() => onFieldDelete(field.id)}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </ResponsiveGridLayout>
    </div>
  );
};

export default GridCanvas;
